import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, nanoid } from '@reduxjs/toolkit';

import type { AppDispatch } from '@/store';

export interface ErrorItem {
    id: string;
    code: number;
    message: string;
}
export interface IApiState {
    remoteSessionExpired: boolean;
    errors: Array<ErrorItem>;
}

export interface IApiStore {
    customApi: IApiState;
}

const initialState: IApiState = {
    remoteSessionExpired: false,
    errors: [],
};

export const apiSlice = createSlice({
    name: 'customApi',
    initialState,
    reducers: {
        setRemoteSessionExpired: (state, action: PayloadAction<boolean>) => {
            state.remoteSessionExpired = action.payload;
        },
        pushError: {
            reducer: (state, action: PayloadAction<ErrorItem>) => {
                const { code, message } = action.payload;
                if (
                    !state.errors.some(
                        (e) => e.code === code && e.message === message,
                    )
                ) {
                    state.errors.push(action.payload);
                }
            },
            prepare: (code: number, message: string) => ({
                payload: { id: nanoid(), code, message },
            }),
        },
        closeError: (state, action: PayloadAction<string>) => {
            state.errors = state.errors.filter((e) => e.id !== action.payload);
        },
        clearErrors: (state) => {
            state.errors = [];
        },
    },
});

export const { setRemoteSessionExpired, pushError, closeError, clearErrors } =
    apiSlice.actions;
export default apiSlice.reducer;

/* Selector */
export const customApiSelectors = ({ customApi }: IApiStore): IApiState =>
    customApi;

/* Dispatch */
export const customApiDispatch = (dispatch: AppDispatch) => ({
    setRemoteSessionExpired: (flag: boolean) =>
        dispatch(setRemoteSessionExpired(flag)),
    pushError: (code: number, message: string) =>
        dispatch(pushError(code, message)),
    closeError: (id: string) => dispatch(closeError(id)),
    clearErrors: () => dispatch(clearErrors()),
});
